import type { StandardTextFieldProps } from '@mui/material';
import { TextField } from '@shared/ui/Inputs';
import { get } from 'lodash-es';
import { useFormContext } from 'react-hook-form';
import { useTexts } from '@/feature/lang';
import type { InputProps } from './types';
import { useValidation } from './useValidation';

function getErrorForField(fieldName: string, errors: any) {
  if (!fieldName) {
    return;
  }

  return get(errors, fieldName);
}

function Input({
  registerControl,
  validation: validationBase,
  margin = 'normal',
  variant = 'filled',
  label,
  placeholder,
  fullWidth = true,
  loading,
  ...rest
}: InputProps) {
  const texts = useTexts();
  const validation = useValidation(validationBase, { label });

  const props = {
    fullWidth,
    margin,
    variant,
    label,
    placeholder,
    loading,
    requiredLabel: texts.component.form.label.required,
    showRequiredLabel: Boolean(validation?.required),
    ...rest,
    slotProps: {
      ...rest.slotProps,
      input: {
        ...rest.slotProps?.input,
      },
    },
  };

  return registerControl ? (
    <RegisteredControl {...props} validation={validation} />
  ) : (
    <BasicInput {...props} />
  );
}

export default Input;

function BasicInput({ ...props }: any) {
  return <TextField {...props} />;
}

function RegisteredControl({ name, validation, ...props }: any) {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const error = getErrorForField(name, errors);
  const { ref, ...inputProps } = register(name, validation);

  return (
    <TextField
      {...props}
      error={Boolean(error)}
      helperText={error && (error.message || 'validation error')}
      inputRef={ref}
      slotProps={{
        ...props.slotProps,
        input: {
          ...props.slotProps?.input,
          ...inputProps,
        },
      }}
    />
  );
}

/**
 * Adapter for call sites still using MUI v5/v6 prop names (InputProps,
 * InputLabelProps, inputProps). MUI v7 replaced these with slotProps, but
 * Autocomplete's renderInput callback still provides the legacy names.
 */
type LegacyInputProps = Omit<InputProps, 'slotProps'> & {
  InputProps?: StandardTextFieldProps['InputProps'];
  InputLabelProps?: StandardTextFieldProps['InputLabelProps'];
  inputProps?: StandardTextFieldProps['inputProps'];
};

export const LegacyInput = ({
  InputProps,
  InputLabelProps,
  inputProps,
  ...rest
}: LegacyInputProps) => {
  return (
    <Input
      {...rest}
      slotProps={{
        input: InputProps,
        inputLabel: InputLabelProps,
        htmlInput: inputProps,
      }}
    />
  );
};
